<template>
    <div class="debug-tool">
        <div class="left">
            <h3>{{ $t("message.actions") }}</h3>
            <div class="buttons">
                <SupsisButton
                    :primary="currentCommand === CommandTypes.GET_ONLINE_USERS_FROM_REDIS"
                    :ghost="currentCommand !== CommandTypes.GET_ONLINE_USERS_FROM_REDIS"
                    small
                    @click="() => run(CommandTypes.GET_ONLINE_USERS_FROM_REDIS)"
                >
                    {{ $t("message.onlineUsersRedis") }}
                </SupsisButton>
                <SupsisButton
                    :primary="currentCommand === CommandTypes.GET_ALL_USERS_FROM_MONGO"
                    :ghost="currentCommand !== CommandTypes.GET_ALL_USERS_FROM_MONGO"
                    small
                    @click="() => run(CommandTypes.GET_ALL_USERS_FROM_MONGO)"
                >
                    {{ $t("message.allUsersMongo") }}
                </SupsisButton>
                <SupsisButton
                    :primary="currentCommand === CommandTypes.AGENT_READ_VUEX"
                    :ghost="currentCommand !== CommandTypes.AGENT_READ_VUEX"
                    small
                    @click="changeCommand(CommandTypes.AGENT_READ_VUEX)"
                >
                    {{ $t("message.readAgentVuex") }}
                </SupsisButton>
                <SupsisButton
                    :primary="currentCommand === CommandTypes.AGENT_IS_CONNECTED"
                    :ghost="currentCommand !== CommandTypes.AGENT_IS_CONNECTED"
                    small
                    @click="changeCommand(CommandTypes.AGENT_IS_CONNECTED)"
                >
                    {{ $t("message.isAgentConnected") }}
                </SupsisButton>
                <SupsisButton
                    :primary="currentCommand === CommandTypes.AGENT_SS"
                    :ghost="currentCommand !== CommandTypes.AGENT_SS"
                    small
                    @click="changeCommand(CommandTypes.AGENT_SS)"
                >
                    {{ $t("message.screenshot") }}
                </SupsisButton>
            </div>
        </div>

        <div class="right">
            <div class="toolbar d-flex gap-5">
                <h3>{{ $t("message.response") }}</h3>
                <div v-if="currentCommand === CommandTypes.AGENT_READ_VUEX" class="d-flex gap-5">
                    <CustomPicker
                        :items="getUserItems()"
                        id-key="id"
                        label-key="label"
                        v-model="selectedUserIds"
                        :title="$t('message.selectAgent')"
                    />
                    <CustomPicker
                        :items="getStorePaths()"
                        id-key="id"
                        label-key="label"
                        v-model="selectedPath"
                        :title="$t('message.selectPath')"
                    />
                    <SupsisButton primary @click="() => run()"> {{ $t("message.run") }}</SupsisButton>
                </div>
                <div v-else-if="currentCommand === CommandTypes.AGENT_IS_CONNECTED" class="d-flex gap-5">
                    <CustomPicker
                        :items="getUserItems()"
                        id-key="id"
                        label-key="label"
                        v-model="selectedUserIds"
                        :title="$t('message.selectAgent')"
                    />
                    <SupsisButton primary @click="() => run()"> {{ $t("message.run") }}</SupsisButton>
                </div>
                <div v-else-if="currentCommand === CommandTypes.GET_ONLINE_USERS_FROM_REDIS" class="d-flex gap-5">
                    <SupsisButton primary @click="() => minifyUsers()"> {{ $t("message.minifyUsers") }}</SupsisButton>
                </div>
                <div v-else-if="currentCommand === CommandTypes.GET_ALL_USERS_FROM_MONGO" class="d-flex gap-5">
                    <SupsisButton primary @click="() => minifyUsers()"> {{ $t("message.minifyUsers") }}</SupsisButton>
                </div>
                <div v-else-if="currentCommand === CommandTypes.AGENT_SS" class="d-flex gap-5">
                    <CustomPicker
                        :items="getUserItems()"
                        id-key="id"
                        label-key="label"
                        v-model="selectedUserIds"
                        :title="$t('message.selectAgent')"
                    />
                    <SupsisButton primary @click="() => run()"> {{ $t("message.run") }}</SupsisButton>
                </div>
            </div>

            <div class="response">
                <span class="content" v-if="busy"> {{ $t("message.waiting") }} </span>
                <div class="content" v-else>
                    <div v-if="currentCommand === CommandTypes.AGENT_SS">
                        <div class="d-flex gap-5">
                            <h1>Screenshot of {{ $_.get(response, `results.${ssIndex}.name`) }}</h1>
                            <div class="d-flex gap-5">
                                <SupsisButton @click="() => (ssIndex = Math.max(0, ssIndex - 1))">{{ $t("message.prev") }}</SupsisButton>
                                <SupsisButton
                                    @click="() => (ssIndex = Math.min(response.results.length - 1, ssIndex + 1))"
                                >
                                    {{ $t("message.next") }}
                                </SupsisButton>
                            </div>
                        </div>

                        <img :src="$_.get(response, `results.${ssIndex}.screenshot`)" />
                    </div>

                    <vue-json-pretty :data="response" v-else />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import _ from "lodash";
import { generateRandomId } from "../utils/session";
import { sendSocketMessage, socketMessageStream } from "../utils/socketStream";
import { filter, take, first, timeout, map } from "rxjs/operators";
import log2 from "../utils/log2";

import SupsisButton from "../views/Pages/Settings/components/SupsisButton.vue";

import VueJsonPretty from "vue-json-pretty";
import "vue-json-pretty/lib/styles.css";

const log = log2("WhatsappDebugTool");
const CommandTypes = {
    GET_ONLINE_USERS_FROM_REDIS: "GET_ONLINE_USERS_FROM_REDIS",
    GET_ALL_USERS_FROM_MONGO: "GET_ALL_USERS_FROM_MONGO",
    AGENT_READ_VUEX: "AGENT_READ_VUEX",
    AGENT_IS_CONNECTED: "AGENT_IS_CONNECTED",
    AGENT_SS: "AGENT_SS",
};

import CustomPicker from "./pickers/CustomPicker.vue";

export default {
    name: "DebugTool",
    data() {
        return {
            busy: false,
            response: undefined,
            CommandTypes,
            pathPickerVisible: false,
            selectedPath: null,
            selectedUserIds: null,
            currentCommand: null,
            ssIndex: 0,
        };
    },
    components: {
        CustomPicker,
        VueJsonPretty,
        SupsisButton,
    },
    computed: {
        ...mapGetters({
            isHealthy: "whatsapp/isHealthy",
        }),
        ...mapState({
            siteId: (state) => state.site.site._id,
            users: (state) => state.user.users.data,
        }),
    },

    methods: {
        getStorePaths() {
            const store = require("@/store").default;
            const modules = [
                "customerWebhook",
                "tag",
                "app",
                "auth",
                "team",
                "user",
                "role",
                "chat",
                "layout",
                "report",
                "subject",
                "visitor",
                "content",
                "setting",
                "permission",
                "language",
                "bannedWord",
                "welcomeMessage",
                "adminUser",
                "site",
                "offlineSupportRequest",
                "socketStatus",
                "customLoginForm",
                "catalog",
                "catalogCategory",
                "catalogContent",
                "globalSettings",
                "ticket",
                "webrtc",
                "whatsapp",
                "instagram",
                "telegrambot",
                "trigger",
                "announcement",
                "CB",
                "contactProperty",
                "channel",
                "mail",
                "wallet",
                "calendar",
                "tasks",
                "favFilters",
            ];
            const state = store.state;
            const paths = [];
            const moduleKeys = modules.reduce((acc, module) => {
                acc[module] = Object.keys(state[module]);
                Object.keys(state[module]).forEach((key) => {
                    paths.push(`${module}.${key}`);
                });
                return acc;
            }, {});
            log("state", { state, keys: Object.keys(state), moduleKeys });

            return paths.map((path) => {
                return {
                    id: path,
                    label: path,
                };
            });
        },
        getUserItems() {
            return [
                {
                    id: "*",
                    label: "All",
                },

                ...this.users.map((user) => {
                    return {
                        id: user._id,
                        label: user.name,
                    };
                }),
            ];
        },

        createPayload(command) {
            if (command === CommandTypes.AGENT_IS_CONNECTED) {
                let userIds = this.selectedUserIds;
                userIds = userIds === "*" ? this.users.map((x) => x._id).join(",") : userIds;
                return { userIds };
            } else if (command === CommandTypes.AGENT_READ_VUEX) {
                let userIds = this.selectedUserIds;
                userIds = userIds === "*" ? this.users.map((x) => x._id).join(",") : userIds;
                return { userIds, path: this.selectedPath };
            } else if (command === CommandTypes.AGENT_SS) {
                let userIds = this.selectedUserIds;
                userIds = userIds === "*" ? this.users.map((x) => x._id).join(",") : userIds;
                return { userIds, path: this.selectedPath };
            } else {
                return {};
            }
        },
        run(command) {
            command = command || this.currentCommand;
            this.changeCommand(command);
            this.sendRequest(command, this.createPayload(command));
        },
        minifyUsers() {
            const { users } = this.response;
            const minifiedUser = users.map((user) => {
                return {
                    _id: user._id,
                    name: user.name,
                    email: user.email,
                    status: user.status,
                    isOnline: user.isOnline,
                };
            });
            this.response = {
                users: minifiedUser,
            };
        },
        changeCommand(command) {
            log("changeCommand", command);
            if (this.currentCommand !== command) {
                this.currentCommand = command;
                if (command === CommandTypes.AGENT_SS) {
                    this.selectedUserIds = this.users[0]._id;
                }
                if (command === CommandTypes.AGENT_READ_VUEX) {
                    this.selectedUserIds = this.users[0]._id;
                }
                if (command === CommandTypes.AGENT_IS_CONNECTED) {
                    this.selectedUserIds = "*";
                }
            }
        },
        sendRequest(command, payload) {
            this.performAction(command, payload);
        },
        performAction(command, payload) {
            log("performAction", { command, payload });
            const messageId = generateRandomId(5);

            this.busy = true;
            socketMessageStream
                .pipe(
                    filter((x) => {
                        const { cmd, data } = x;
                        log("socket message", { cmd, data });
                        return cmd === "debug" && data.messageId === messageId;
                    }),
                    first(),
                    timeout(30000)
                )
                .toPromise()
                .then(({ data }) => {
                    log("response received", data);
                    const { response, success, error } = data;
                    this.response = success ? response : error;
                })
                .catch((err) => {
                    console.error(err);
                    this.response = "Request timed out";
                })
                .finally(() => {
                    this.busy = false;
                });

            sendSocketMessage("debug run-command", {
                siteId: this.siteId,
                command,
                messageId,
                payload,
            });
        },
    },
    mounted() {
        log("mounted");
    },
    updated() {
        log("updated", this);
    },
    watch: {},
};
</script>

<style lang="scss">
.debug-tool {
    min-width: 90vw !important;
    min-height: 90vh !important;
    display: flex;
    flex-direction: row;

    .left {
        display: flex;
        flex-direction: column;
        gap: 5px;
        padding: 10px;
        border-right: 1px solid #d1dfec;
        width: 200px;

        .buttons {
            width: 200px;
            display: flex;
            flex-direction: column;
            gap: 5px;
        }
    }

    .right {
        display: flex;
        flex-direction: column;
        gap: 5px;
        padding: 10px;
        width: calc(100% - 200px);

        .response {
            width: 100%;
            height: auto;
            background-color: #f5f8fb;
            border: 1px solid #d1dfec;
            display: flex;
            position: relative;
            flex-direction: column;

            .title {
                padding: 5px;
                font-weight: 600;
                width: 100%;
                display: flex;
                justify-content: flex-end;
            }

            .content {
                padding: 5px;
                font-weight: 600;
                width: 100%;
                display: flex;
                justify-content: flex-start;
                color: #0e4d73;
            }
        }
    }

    .my-editor {
        max-height: 89vh;
    }
}

.debug-tool-modal-wrapper {
    .ant-modal {
        min-width: 95vw !important;
        top: 10px !important;
    }

    .ant-modal-content {
        min-height: 90vh !important;
    }
}
</style>
